// -----------------------
// Check details on reparametrization
// https://www.w3.org/TR/SVG11/implnote.html#ArcImplementationNotes
//
// @see https://observablehq.com/@toja/ellipse-and-elliptical-arc-conversion
// @see https://observablehq.com/@awhitty/svg-2-elliptical-arc-to-canvas-path2d
// -----------------------
const pow = (value) => Math.pow(value, 2);
const deg = (value) => (value * 180) / Math.PI;
const rad = (value) => (value * Math.PI) / 180;
function vectorAngle(vecu, vecv) {
    const [ux, uy] = vecu;
    const [vx, vy] = vecv;
    const { acos, sqrt } = Math;
    const sign = ux * vy - uy * vx < 0 ? -1 : 1;
    const ua = sqrt(ux * ux + uy * uy);
    const va = sqrt(vx * vx + vy * vy);
    const dot = ux * vx + uy * vy;
    return sign * acos(dot / (ua * va));
}
/**
 * Converts arc from endpoint to center point
 * @param x1 start point X coord
 * @param y1 start point Y coord
 * @param x2 end point X coord
 * @param y2 end point Y coord
 * @param fa big circle
 * @param fs
 * @param rx X radius
 * @param ry Y radius
 * @param phi ellipse rotation
 * @returns
 */
export function getCenterParameters(x1, y1, x2, y2, fa, fs, rx, ry, phi) {
    const { abs, sin, cos, sqrt } = Math;
    const sinphi = sin(phi);
    const cosphi = cos(phi);
    // Step 1: simplify through translation/rotation
    const x = (cosphi * (x1 - x2)) / 2 + (sinphi * (y1 - y2)) / 2;
    const y = (-sinphi * (x1 - x2)) / 2 + (cosphi * (y1 - y2)) / 2;
    const px = pow(x);
    const py = pow(y);
    const prx = pow(rx);
    const pry = pow(ry);
    // correct of out-of-range radii
    const orr = px / prx + py / pry;
    if (orr > 1) {
        rx = sqrt(orr) * abs(rx);
        ry = sqrt(orr) * abs(ry);
    }
    else {
        rx = abs(rx);
        ry = abs(ry);
    }
    // Step 2 + 3: compute center
    const sign = fa === fs ? -1 : 1;
    const Mp = sqrt((prx * pry - prx * py - pry * px) / (prx * py + pry * px)) * sign;
    const cxp = (Mp * (rx * y)) / ry;
    const cyp = (Mp * (-ry * x)) / rx;
    const cx = cosphi * cxp - sinphi * cyp + (x1 + x2) / 2;
    const cy = sinphi * cxp + cosphi * cyp + (y1 + y2) / 2;
    // Step 4: compute θ and dθ
    const theta = vectorAngle([1, 0], [(x - cxp) / rx, (y - cyp) / ry]);
    let dTheta = deg(vectorAngle([(x - cxp) / rx, (y - cyp) / ry], [(-x - cxp) / rx, (-y - cyp) / ry])) % 360;
    if (fs === 0 && dTheta > 0) {
        dTheta -= 360;
    }
    if (fs === 1 && dTheta < 0) {
        dTheta += 360;
    }
    return { cx, cy, theta, phi: theta + rad(dTheta) };
}
